import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@modules/auth/hooks/context/AuthContext';
import LoadingScreen from './LoadingScreen';
import Forbidden from '@app/components/errors/403';
import { PermissionRequirement } from '@modules/auth/lib/permission.shared';
import {useTranslation} from 'react-i18next';

interface RouteGuardProps {
    children: React.ReactElement;
    permissions?: PermissionRequirement;
    anyPermissions?: PermissionRequirement[];
    fallback?: React.ReactElement | null;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ children, permissions, anyPermissions, fallback = null }) => {
    const {t} = useTranslation();
    const { isAuthenticated, loading, hasPermission, serverRetryWarning } = useAuth();
    const location = useLocation();
    const isLoading = loading.useIsLoading();

    if (isLoading) {
        return <LoadingScreen textContent={t('common.session-verification')} warning={serverRetryWarning} />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/auth" state={{ from: location }} replace />;
    }

    if ((permissions && !hasPermission(permissions)) ||
        (anyPermissions?.length && !anyPermissions.some(hasPermission))) {
        return fallback ?? <Forbidden />;
    }

    return children;
};

export default RouteGuard;
